import * as z from "@-/com.npmjs.zod";

/**
 * What the onboarding reacts to, each once, in whatever order the learner gets there. A reaction
 * is recorded once it has played to its end: one the learner walks away from (switching tabs
 * part-way through an introduction) plays again, from its start, the next time its trigger fires.
 *
 * - `intro`: the account was created → the globe and its questions, over the whole app, which end
 *   with the globe landing on Learn's map.
 * - `map`: the map, first seen → the sheet says what the map is for. Unlocks Browse.
 * - `browse`: Browse, first opened → characters, pinyin and the Ready pack. Unlocks Learn (the
 *   map's tab turns into Learn's).
 * - `cards`: the cards, first reached → the tour is over: the top-left gets its More menu back, and
 *   the map its own sheet.
 * - `first-character`: the first character learned → unlocks Stats.
 * - `stats`: Stats, first opened → what it counts. Unlocks Deck.
 * - `decks`: Deck, first opened → how reviews work.
 * - `done`: the first level learned (or an account that had learned before) → the onboarding is
 *   over, and the strip is the learner's own again (Search included).
 */
export const ONBOARDING_REACTIONS = [
  "intro",
  "map",
  "browse",
  "cards",
  "first-character",
  "stats",
  "decks",
  "done",
] as const;
export type OnboardingReaction = (typeof ONBOARDING_REACTIONS)[number];

/** The destinations a reaction unlocks, which the sheet announces ("Go to Stats"). */
export const ONBOARDING_ANNOUNCED_DESTINATIONS = ["browse", "learn", "stats", "decks"] as const;
export type OnboardingAnnouncedDestination = (typeof ONBOARDING_ANNOUNCED_DESTINATIONS)[number];

export const onboardingStateSchema = z.object({
  /**
   * Announced destinations the learner has answered: followed there, or passed over by switching
   * to another tab while the announcement showed. Either way it is not shown again.
   */
  answeredAnnouncements: z.array(z.enum(ONBOARDING_ANNOUNCED_DESTINATIONS)).default([]),
  /** "What's your daily learning goal?", in minutes a day. */
  dailyGoalMinutes: z.number().int().positive().nullable().default(null),
  /** "How did you hear about Haniverse?": an option id of the onboarding's `heard-about` page. */
  heardAbout: z.string().nullable().default(null),
  /** The reactions that have played to their end (`ONBOARDING_REACTIONS`). */
  reactions: z.array(z.enum(ONBOARDING_REACTIONS)).default([]),
  /** "Why are you learning Chinese?": option ids of the onboarding's `reasons` page. */
  reasons: z.array(z.string()).default([]),
  /**
   * Strip destinations whose arrival has been shown. A destination the onboarding unlocks pops
   * into the strip once, then stays.
   */
  revealedDestinations: z.array(z.string()).default([]),
  /** When the onboarding began (ISO 8601). */
  startedAt: z.string(),
});
export type OnboardingState = z.infer<typeof onboardingStateSchema>;

export function createOnboardingState(startedAt: string): OnboardingState {
  return {
    answeredAnnouncements: [],
    dailyGoalMinutes: null,
    heardAbout: null,
    reactions: [],
    reasons: [],
    revealedDestinations: [],
    startedAt,
  };
}

export function hasOnboardingReactionPlayed(
  state: OnboardingState,
  reaction: OnboardingReaction,
): boolean {
  return state.reactions.includes(reaction);
}

/**
 * `state` with `reaction` recorded as played, or `state` itself if it already was (a double tap, a
 * progress check racing a press).
 */
export function playOnboardingStateReaction(
  state: OnboardingState,
  reaction: OnboardingReaction,
): OnboardingState {
  return hasOnboardingReactionPlayed(state, reaction)
    ? state
    : { ...state, reactions: [...state.reactions, reaction] };
}

/** `state` with `destination`'s announcement answered, or `state` itself if it already was. */
export function answerOnboardingStateAnnouncement(
  state: OnboardingState,
  destination: OnboardingAnnouncedDestination,
): OnboardingState {
  return state.answeredAnnouncements.includes(destination)
    ? state
    : { ...state, answeredAnnouncements: [...state.answeredAnnouncements, destination] };
}

/** Whether the onboarding still shapes the app (its strip, its sheet, its overlay). */
export function isOnboardingActive(state: OnboardingState | null): state is OnboardingState {
  return state !== null && !hasOnboardingReactionPlayed(state, "done");
}

/** Whether the globe and its questions still cover the app. */
export function isOnboardingIntro(state: OnboardingState | null): boolean {
  return isOnboardingActive(state) && !hasOnboardingReactionPlayed(state, "intro");
}

/**
 * Whether the onboarding is still touring the app (its globe, then its sheet), rather than
 * studying: the top-left keeps its More menu to itself, and the map its own sheet, until the
 * learner reaches their cards (or learns a character some other way).
 */
export function isOnboardingTour(state: OnboardingState | null): boolean {
  return (
    isOnboardingActive(state) &&
    !hasOnboardingReactionPlayed(state, "cards") &&
    !hasOnboardingReactionPlayed(state, "first-character")
  );
}
